// Pet-photo trap (2026-07-12): client-side "does this upload look like a
// photo?" classifier for the LOGO flow.
//
// Why: customers upload pet PHOTOS on standard logo-flow products, tap
// the "Match the design" thread option, and expect a coloured + filled
// portrait without the pet-portrait fee. The logo flow has no fill
// capability, so PlacementSection runs this classifier on every fresh
// logo-flow upload (only when config.photoUpgrade is wired) and, on a
// photo-like result, offers the artist-redraw Portrait upgrade in place.
//
// How: draw the image to an offscreen 64x64 canvas and score the pixels
// three ways (all pure maths, unit-tested in photoDetect.test.ts):
//   - distinctColours: distinct quantised colours at 4 bits/channel.
//     Flat logo art (even 20-30 solid colours) stays low - anti-aliased
//     edges add blend buckets but the art is dominated by runs of the
//     same colour. Photos carry continuous tone, so almost every region
//     contributes fresh buckets (typically 800-2500 of the 4096
//     possible at this sample size).
//   - gradientScore: mean absolute luminance difference between each
//     pixel and its right + bottom neighbours. Logo art is mostly
//     0-difference interior with sparse hard edges (mean ~1-5); photo
//     texture puts gradient nearly everywhere (mean ~8-25).
//   - flatShare: share of near-white or transparent pixels. Logos
//     usually sit on a flat background (>50% of the frame); photos
//     rarely do.
//
// Classification is deliberately CONSERVATIVE - all three conditions
// must hold, so real logos never trigger the card. A missed photo just
// means no offer (status quo), which is the cheap failure mode.
//
// isPhotoLike() carries a hard time budget: any error, non-image file
// (PDF), tainted canvas, or slow decode resolves false (= skip).

export type PhotoScore = {
  /** Distinct quantised colours (4 bits/channel) among visible pixels. */
  distinctColours: number;
  /** Mean |luminance delta| to right + bottom neighbours (0-255 scale). */
  gradientScore: number;
  /** Share (0-1) of near-white or near-transparent pixels. */
  flatShare: number;
};

/** Downsample target. 64x64 = 4096 samples: enough signal to separate
 *  continuous tone from flat art, small enough to score in ~1ms. */
export const PHOTO_DETECT_SIZE = 64;

/** Hard wall-clock budget for the whole classify pass (decode + draw +
 *  score). On timeout the answer is false (skip the offer). */
export const PHOTO_DETECT_BUDGET_MS = 300;

// Thresholds. Tuned conservatively so flat-colour logo art (even with
// 20-30 colours and anti-aliased edges) NEVER classifies as a photo;
// see the header comment for the per-signal reasoning.
export const PHOTO_MIN_DISTINCT_COLOURS = 350;
export const PHOTO_MIN_GRADIENT_SCORE = 6;
export const PHOTO_MAX_FLAT_SHARE = 0.45;

/** Alpha below this = "transparent" (counts as flat background and is
 *  excluded from the colour census). */
const ALPHA_VISIBLE_MIN = 16;
/** r, g and b all at/above this = "near-white" background pixel. */
const NEAR_WHITE_MIN = 240;

/** Pure scorer over an RGBA pixel array (length width*height*4). Split
 *  from the canvas plumbing so it can be unit-tested with synthetic
 *  arrays in a node environment. */
export function scorePixels(
  data: Uint8ClampedArray | number[],
  width: number,
  height: number,
): PhotoScore {
  const total = width * height;
  if (total === 0) {
    return { distinctColours: 0, gradientScore: 0, flatShare: 1 };
  }

  const buckets = new Set<number>();
  let flat = 0;
  // Luminance grid for the gradient pass. Transparent pixels read as
  // white so a logo's edge against a transparent background registers
  // the same as one against a white background.
  const lum = new Float32Array(total);

  for (let p = 0; p < total; p++) {
    const i = p * 4;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (a < ALPHA_VISIBLE_MIN) {
      flat++;
      lum[p] = 255;
      continue;
    }
    if (r >= NEAR_WHITE_MIN && g >= NEAR_WHITE_MIN && b >= NEAR_WHITE_MIN) {
      flat++;
    }
    buckets.add(((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4));
    lum[p] = 0.299 * r + 0.587 * g + 0.114 * b;
  }

  let gradientSum = 0;
  let gradientCount = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const p = y * width + x;
      if (x + 1 < width) {
        gradientSum += Math.abs(lum[p] - lum[p + 1]);
        gradientCount++;
      }
      if (y + 1 < height) {
        gradientSum += Math.abs(lum[p] - lum[p + width]);
        gradientCount++;
      }
    }
  }

  return {
    distinctColours: buckets.size,
    gradientScore: gradientCount > 0 ? gradientSum / gradientCount : 0,
    flatShare: flat / total,
  };
}

/** Pure threshold check. All three signals must agree it's a photo. */
export function isPhotoLikeScore(score: PhotoScore): boolean {
  return (
    score.distinctColours > PHOTO_MIN_DISTINCT_COLOURS &&
    score.gradientScore > PHOTO_MIN_GRADIENT_SCORE &&
    score.flatShare < PHOTO_MAX_FLAT_SHARE
  );
}

// Decode a source into something drawImage accepts. A File/Blob goes
// through createImageBitmap (rejects non-images like PDF - the caller
// treats that as "not a photo"). A URL string loads via an Image with
// crossOrigin so getImageData doesn't taint the canvas when the staging
// file lives on the worker origin.
async function decodeSource(source: string | Blob): Promise<CanvasImageSource> {
  if (typeof source !== "string") {
    return await createImageBitmap(source);
  }
  return await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("photo_detect_image_load_failed"));
    img.src = source;
  });
}

async function classify(source: string | Blob): Promise<boolean> {
  const bitmap = await decodeSource(source);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = PHOTO_DETECT_SIZE;
    canvas.height = PHOTO_DETECT_SIZE;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return false;
    ctx.drawImage(bitmap, 0, 0, PHOTO_DETECT_SIZE, PHOTO_DETECT_SIZE);
    const { data } = ctx.getImageData(
      0,
      0,
      PHOTO_DETECT_SIZE,
      PHOTO_DETECT_SIZE,
    );
    return isPhotoLikeScore(
      scorePixels(data, PHOTO_DETECT_SIZE, PHOTO_DETECT_SIZE),
    );
  } finally {
    if (typeof (bitmap as ImageBitmap).close === "function") {
      (bitmap as ImageBitmap).close();
    }
  }
}

/** True when the image at `source` (URL or File/Blob) looks like a
 *  photo. Hard PHOTO_DETECT_BUDGET_MS budget; any timeout, decode
 *  failure, non-image file or canvas error resolves false so the caller
 *  simply skips the offer. Never throws. */
export async function isPhotoLike(source: string | Blob): Promise<boolean> {
  if (typeof document === "undefined") return false;
  try {
    return await Promise.race([
      classify(source),
      new Promise<boolean>((resolve) => {
        setTimeout(() => resolve(false), PHOTO_DETECT_BUDGET_MS);
      }),
    ]);
  } catch {
    return false;
  }
}
